import { createFileRoute, Link } from '@tanstack/react-router';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { ADRS } from '../lib/adrs.js';

export const Route = createFileRoute('/adrs/')({
  component: AdrIndexPage,
});

function AdrIndexPage() {
  usePageTitle('Design notes');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Design notes
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Architecture decisions, written down.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            A small set of decisions we&rsquo;ve made about how Bookaburra
            works under the hood. Written up the same way we use them
            internally &mdash; context, decision, trade-offs we accepted,
            and the alternatives we considered and rejected.
          </p>
          <div className="mt-8 max-w-2xl rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-6 text-base text-brand-ink-soft/80 leading-relaxed">
            <p>
              We publish these because the trade-offs matter. Most
              architectural calls aren&rsquo;t obvious in hindsight; the
              reasoning behind them is the part that ages best, and the
              part most likely to be useful to someone else weighing the
              same problem.
            </p>
          </div>
          <div className="mt-8">
            <Link
              to="/technology"
              className="text-sm font-semibold text-brand-sage underline-offset-2 hover:underline"
            >
              &larr; Back to the technology stack
            </Link>
          </div>
        </div>
      </section>

      {/* ADR LIST */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>Published ADRs</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            The current set.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            The repo holds more decisions than these &mdash; database
            choice, auth library, hosting platform, and so on. We
            surface here only the ones with broader interest beyond the
            day-to-day build.
          </p>
        </div>
        <ul className="mt-12 grid gap-5">
          {ADRS.map((adr) => (
            <li key={adr.slug}>
              <Link
                to="/adrs/$slug"
                params={{ slug: adr.slug }}
                className="block rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-brand transition hover:border-brand-sage/40"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
                    ADR-{adr.number}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-brand-sage/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage">
                    {adr.status}
                  </span>
                </div>
                <h3 className="mt-4 text-xl font-bold tracking-tight text-brand-ink-soft sm:text-2xl">
                  {adr.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                  {adr.summary}
                </p>
                <p className="mt-4 text-sm font-semibold text-brand-sage">
                  Read the decision &rarr;
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
            Want to talk to the person who made these calls?
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-brand-cream/80">
            Architecture decisions are easier to evaluate in conversation
            than in writing.{' '}
            <Link
              to="/contact"
              className="font-medium underline-offset-4 hover:underline"
            >
              Get in touch
            </Link>
            {' '}&mdash; happy to walk through any of these.
          </p>
        </div>
      </section>
    </>
  );
}
